import type { Locale } from "@/lib/i18n/config";

/**
 * Mandatory "Sponsored / Iklan" label on every paid/affiliate unit.
 *
 * Honest disclosure is the delight moat — an unlabelled ad on a job site reads
 * as scammy and undermines the trust the B2B side needs. Bilingual for
 * FTC (en) + Kominfo (id) expectations.
 *
 * `advertiseHref`, when set, renders a small "Advertise here / Pasang iklan"
 * link next to the chip — the openness lever: every labelled unit is also a
 * lead-gen unit for /advertise. `relative z-10` matches `RemoveAdsLink`
 * (`./remove-ads-link.tsx`): several call sites stretch the unit's whole CTA
 * over the card with `after:absolute after:inset-0`, and without the raised
 * stacking context a tap here would open the partner link instead.
 */
export function SponsoredLabel({
	locale = "id",
	className,
	advertiseHref,
}: {
	locale?: Locale;
	className?: string;
	advertiseHref?: string;
}) {
	const text = locale === "en" ? "Sponsored" : "Iklan";
	const chip = (
		<span
			className={[
				"inline-flex items-center rounded-sm bg-muted px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground",
				className,
			]
				.filter(Boolean)
				.join(" ")}
		>
			{text}
		</span>
	);

	if (!advertiseHref) return chip;

	return (
		<span className="inline-flex items-center">
			{chip}
			<a
				href={advertiseHref}
				className="relative z-10 ml-2 rounded-sm text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
			>
				{locale === "en" ? "Advertise here" : "Pasang iklan"}
			</a>
		</span>
	);
}
